/* ==========================================================================
   PigPole — Custom Collection AJAX
   --------------------------------------------------------------------------
   Powers in-place updates of the Custom Collection toolbar + grid sections
   without a full page reload. Three behaviors:

   1. Filter form change       → fetch & swap toolbar + grid
   2. Sort change              → fetch & swap toolbar + grid
   3. Active chip click        → fetch & swap toolbar + grid
   4. "Load more" click        → fetch next page, append cards into the grid

   Pagination still works as a server-rendered fallback. If JS fails or is
   blocked, the underlying <form> action and <a href> attributes do the
   right thing on a normal page reload.

   The toolbar tells us the grid section id via data-cct-section. The grid
   is identified by id="CustomCollectionGrid-<sectionId>" and the toolbar
   by id="CustomCollectionToolbar-<sectionId>".

   No external dependencies. ES2017+ syntax (async/await, Map, Set).
   ========================================================================== */

(function () {
  'use strict';

  if (window.__pigpoleCollectionAjaxLoaded) return;
  window.__pigpoleCollectionAjaxLoaded = true;

  // --- DOM helpers --------------------------------------------------------
  const qs   = (sel, root) => (root || document).querySelector(sel);
  const qsa  = (sel, root) => Array.from((root || document).querySelectorAll(sel));

  function toolbarEl() { return qs('[data-cct-toolbar]'); }
  function gridEl()    { return qs('[data-ccg-grid]'); }

  // --- Loading state ------------------------------------------------------
  function setLoading(on) {
    const grid = gridEl();
    if (!grid) return;
    grid.toggleAttribute('data-loading', !!on);
  }

  // --- URL helpers --------------------------------------------------------
  function pushUrl(url) {
    try {
      window.history.pushState({}, '', url);
    } catch (_) { /* no-op */ }
  }

  // --- Section fetch + swap ----------------------------------------------
  async function fetchAndSwap(url) {
    const oldToolbar = toolbarEl();
    const oldGrid    = gridEl();
    if (!oldToolbar && !oldGrid) return false;

    const u = new URL(url, window.location.origin);

    // Fetch the full page once, parse the DOM, swap each marked section
    // root in place. The Section Rendering API would be lighter but strips
    // outer wrappers that hold our data-* hooks.
    setLoading(true);

    try {
      const res = await fetch(u.toString(), {
        headers: { 'X-Requested-With': 'XMLHttpRequest', 'Accept': 'text/html' },
        credentials: 'same-origin'
      });
      if (!res.ok) throw new Error('Network response was not ok');

      const html = await res.text();
      const doc  = new DOMParser().parseFromString(html, 'text/html');

      const newToolbar = qs('[data-cct-toolbar]', doc);
      const newGrid    = qs('[data-ccg-grid]', doc);

      if (newToolbar && oldToolbar) oldToolbar.replaceWith(newToolbar);
      if (newGrid && oldGrid)       oldGrid.replaceWith(newGrid);

      pushUrl(u.pathname + u.search);

      // Re-bind any custom-elements that need it (the swapped DOM is a
      // brand-new tree, so the <custom-filters> element re-runs its
      // connectedCallback automatically when inserted).

      // Notify the rest of the page that filters changed.
      document.dispatchEvent(new CustomEvent('collection:filtered', {
        bubbles: true,
        detail: { url: u.toString() }
      }));

      return true;
    } catch (err) {
      console.warn('[custom-collection-ajax] swap failed, falling back to navigation', err);
      window.location.href = u.toString();
      return false;
    } finally {
      setLoading(false);
    }
  }

  // --- Filter form submit -------------------------------------------------
  function buildUrlFromForm(form) {
    const action = form.getAttribute('action') || window.location.pathname;
    const params = new URLSearchParams(new FormData(form));
    // Strip empty params for cleaner URLs.
    const clean = new URLSearchParams();
    for (const [k, v] of params.entries()) {
      if (v !== '' && v != null) clean.append(k, v);
    }
    const qsStr = clean.toString();
    return action + (qsStr ? ('?' + qsStr) : '');
  }

  // --- Event delegation ---------------------------------------------------
  // We intercept at the `submit` event so the existing inline
  // `onchange="this.form.requestSubmit()"` handlers in the filter inputs
  // still work as the trigger — we just hijack the resulting submit.
  document.addEventListener('submit', function (e) {
    const form = e.target;
    if (!form || !form.matches) return;
    if (!form.matches('[data-ccf-form], [data-cct-sort-form]')) return;
    e.preventDefault();
    fetchAndSwap(buildUrlFromForm(form));
  }, true);

  // Active chip click (remove a single filter).
  document.addEventListener('click', function (e) {
    const chip = e.target && e.target.closest && e.target.closest('[data-cct-chip]');
    if (!chip) return;
    const href = chip.getAttribute('href');
    if (!href) return;
    e.preventDefault();
    fetchAndSwap(href);
  }, true);

  // Clear-all link in the toolbar / filter panel.
  document.addEventListener('click', function (e) {
    const clear = e.target && e.target.closest && e.target.closest('[data-cct-clear], [data-ccf-clear]');
    if (!clear) return;
    const href = clear.getAttribute('href');
    if (!href) return;
    e.preventDefault();
    fetchAndSwap(href);
  }, true);

  // --- Load more ----------------------------------------------------------
  // Click the load-more button to fetch its href, parse out the next page's
  // product cards, append them to the existing grid, then swap the button
  // for the next page's button (or hide it if there's no further page).
  document.addEventListener('click', async function (e) {
    const btn = e.target && e.target.closest && e.target.closest('[data-load-more]');
    if (!btn) return;
    e.preventDefault();

    const url = btn.getAttribute('data-load-more-url');
    if (!url) return;
    if (btn.dataset.busy === '1') return;

    btn.dataset.busy = '1';
    const originalLabel = btn.textContent;
    btn.textContent = btn.getAttribute('data-loading-label') || 'Loading…';
    btn.setAttribute('aria-busy', 'true');

    try {
      const res = await fetch(url, {
        headers: { 'X-Requested-With': 'XMLHttpRequest', 'Accept': 'text/html' },
        credentials: 'same-origin'
      });
      if (!res.ok) throw new Error('Network response was not ok');

      const html = await res.text();
      const doc  = new DOMParser().parseFromString(html, 'text/html');

      const grid          = qs('[data-products-grid]');
      const incomingGrid  = qs('[data-products-grid]', doc);
      const incomingMore  = qs('[data-load-more]', doc);

      if (grid && incomingGrid) {
        // Append every cell from the incoming grid into the live one.
        Array.from(incomingGrid.children).forEach(function (cell) {
          grid.appendChild(cell);
        });
      }

      // Swap (or remove) the load-more button.
      if (incomingMore) {
        btn.setAttribute('data-load-more-url', incomingMore.getAttribute('data-load-more-url') || '');
        btn.textContent = originalLabel;
        btn.setAttribute('aria-busy', 'false');
        btn.dataset.busy = '0';
      } else {
        btn.remove();
      }

      // Update URL to reflect the new "page" so reload won't re-collapse the list.
      pushUrl(url);

      document.dispatchEvent(new CustomEvent('collection:loaded-more', {
        bubbles: true,
        detail: { url: url }
      }));
    } catch (err) {
      console.warn('[custom-collection-ajax] load-more failed', err);
      btn.textContent = btn.getAttribute('data-error-label') || 'Try again';
      btn.setAttribute('aria-busy', 'false');
      btn.dataset.busy = '0';
    }
  }, true);

  // --- Browser back/forward ----------------------------------------------
  window.addEventListener('popstate', function () {
    // Soft re-fetch on back/forward so the toolbar + grid match the URL.
    fetchAndSwap(window.location.href);
  });
})();
